/**
 * Elite Pulse board filters: one pure predicate for every chip and box on the toolbar, plus
 * the persisted form those filters are saved in.
 *
 * Every filter is an AND: a tile must pass each one that is set, and an empty filter (no
 * chips on, blank box) is not a filter at all. Kept out of the component so each predicate
 * can be tested against representative rows.
 */

import { AssetClass, classifyTicker } from './assetClass';
import { CapTier, normalizeCapTier } from './capTier';
import type { ScannerTile } from './rtlScannerTiles';

export interface PulseBoardFilters {
  /** Lower-cased ticker substring. */
  search: string;
  relMin: number;
  premMin: number;
  /** Infinity when no limit is set. */
  dteMax: number;
  money: ReadonlySet<string>;
  horizon: ReadonlySet<string>;
  assetClasses: ReadonlySet<AssetClass>;
  capTiers: ReadonlySet<CapTier>;
}

const ASSET_CLASSES: ReadonlySet<string> = new Set(['stock', 'etf', 'index']);

/**
 * The tile's asset class: the reference class the server stamped on the row, else the local
 * ticker list. The local list names only ~60 ETFs, so on its own it filed sector and
 * thematic ETFs under Stock.
 */
export function tileAssetClass(r: Pick<ScannerTile, 't' | 'assetClass'>): AssetClass {
  const v = String(r.assetClass ?? '').trim().toLowerCase();
  if (ASSET_CLASSES.has(v)) return v as AssetClass;
  return classifyTicker(r.t);
}

export function matchesPulseBoardFilters(r: ScannerTile, f: PulseBoardFilters): boolean {
  if (f.search && !r.t.toLowerCase().includes(f.search)) return false;
  if (f.relMin > 0 && (r.rv == null || r.rv < f.relMin)) return false;
  if (f.premMin > 0 && (r.prem == null || r.prem < f.premMin)) return false;
  if (f.assetClasses.size && !f.assetClasses.has(tileAssetClass(r))) return false;
  if (f.capTiers.size && !f.capTiers.has(normalizeCapTier(r.cap))) return false;
  if (f.money.size && !f.money.has(r.mc)) return false;
  if (f.horizon.size && (!r.horizon_tml || !f.horizon.has(r.horizon_tml))) return false;
  // Only applied when a limit is set: an unknown expiry is not "too far out".
  if (Number.isFinite(f.dteMax) && (r.dte == null || r.dte > f.dteMax)) return false;
  return true;
}

// ── persistence ───────────────────────────────────────────────────────────────

/** What is saved: the raw inputs, so a reload restores the toolbar exactly as it was left. */
export interface StoredPulseFilters {
  search: string;
  relInput: string;
  premInput: string;
  dteInput: string;
  money: string[];
  horizon: string[];
  assetClasses: AssetClass[];
  capTiers: CapTier[];
}

export const EMPTY_STORED_FILTERS: StoredPulseFilters = {
  search: '',
  relInput: '',
  premInput: '',
  dteInput: '',
  money: [],
  horizon: [],
  assetClasses: [],
  capTiers: [],
};

const MONEY_VALUES = new Set(['OTM', 'ATM', 'ITM']);
const HORIZON_VALUES = new Set(['[L]', '[M]', '[T]']);
const CAP_VALUES = new Set<string>(['large', 'medium', 'small']);

function strings(v: unknown, allowed: ReadonlySet<string>): string[] {
  if (!Array.isArray(v)) return [];
  return Array.from(new Set(v.filter((x): x is string => typeof x === 'string' && allowed.has(x))));
}

function str(v: unknown): string {
  return typeof v === 'string' ? v.slice(0, 64) : '';
}

/** Accepts anything (a stale or hand-edited entry); unknown values are dropped, never trusted. */
export function parseStoredPulseFilters(raw: string | null): StoredPulseFilters {
  if (!raw) return { ...EMPTY_STORED_FILTERS };
  try {
    const o = JSON.parse(raw);
    if (!o || typeof o !== 'object') return { ...EMPTY_STORED_FILTERS };
    return {
      search: str(o.search),
      relInput: str(o.relInput),
      premInput: str(o.premInput),
      dteInput: str(o.dteInput),
      money: strings(o.money, MONEY_VALUES),
      horizon: strings(o.horizon, HORIZON_VALUES),
      assetClasses: strings(o.assetClasses, ASSET_CLASSES) as AssetClass[],
      capTiers: strings(o.capTiers, CAP_VALUES) as CapTier[],
    };
  } catch {
    return { ...EMPTY_STORED_FILTERS };
  }
}

/** True when nothing is set, so an untouched toolbar stores nothing. */
export function isEmptyStoredFilters(f: StoredPulseFilters): boolean {
  return (
    !f.search && !f.relInput && !f.premInput && !f.dteInput &&
    !f.money.length && !f.horizon.length && !f.assetClasses.length && !f.capTiers.length
  );
}
